import styles from "./Header.module.scss";
import { useGeoJsonData } from "../../GeoJsonDataContext";
import { MEDIA_BASE_URL } from "../../settings";
import { useState, useEffect } from "react";
// import { isMobile } from "react-device-detect";

/**
 * Page header: title, the "share your appreciation" CTA (disabled now that
 * submissions have closed), and an animated "N messages added on <date> (total M)" counter
 * derived from geoJsonData. Takes no props; reads geoJsonData from context.
 */
const Header = () => {
  const MEDIA_URL = MEDIA_BASE_URL;
  const { geoJsonData } = useGeoJsonData();
  const [latestCount, setLatestCount] = useState(null);
  const [counterText, setCounterText] = useState(null);
  const [animatedValue, setAnimatedValue] = useState(0);

  // Animate from start to end value, calling onUpdate for each step.
  // Returns the interval id (or null if there's nothing to animate) so the
  // caller can cancel it if a new animation needs to start first.
  const animateValue = (start, end, duration, onUpdate) => {
    if (end === start) {
      onUpdate(end);
      return null;
    }
    const range = end - start;
    let current = start;
    const increment = end > start ? 1 : -1;
    const stepTime = Math.abs(Math.floor(duration / range));
    const timer = setInterval(() => {
      current += increment;
      onUpdate(current);
      if (current === end) {
        clearInterval(timer);
      }
    }, stepTime);
    return timer;
  };

  // Use the useEffect hook to animate whenever "end" changes
  useEffect(() => {
    if (latestCount === null) return;
    const timer = animateValue(0, latestCount, 1000, setAnimatedValue);
    // Cancel any still-running animation if latestCount changes again
    // before this one finishes (e.g. geoJsonData reloads).
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [latestCount]);

  useEffect(() => {
    if (geoJsonData && geoJsonData.features.length > 0) {
      let latestDate = null;
      let count = 0;

      const dateCounts = new Map();

      geoJsonData.features.forEach((feature) => {
        const { date } = feature.properties;
        // Update latest date if this date is greater
        if (!latestDate || date > latestDate) {
          latestDate = date;
        }

        // Format date as dd/mm/yy for counting
        const dateStr = `${String(date.getDate()).padStart(2, "0")}/${String(
          date.getMonth() + 1
        ).padStart(2, "0")}/${String(date.getFullYear()).slice(-2)}`;

        // Count the occurrences of each date
        dateCounts.set(dateStr, (dateCounts.get(dateStr) || 0) + 1);
      });

      // Convert latest date to dd/mm/yy format
      const latestDateStr = `${String(latestDate.getDate()).padStart(
        2,
        "0"
      )}/${String(latestDate.getMonth() + 1).padStart(2, "0")}/${String(
        latestDate.getFullYear()
      ).slice(-2)}`;

      // Get the count of features with the latest date
      count = dateCounts.get(latestDateStr) || 0;

      setLatestCount(count);
      setCounterText(
        `messages added on ${latestDateStr} (total ${geoJsonData.features.length.toLocaleString()})`
      );
    }
  }, [geoJsonData]);

  return (
    <div className={styles.Header}>
      <h1 className={styles.heading}>
        World Teachers’ Day 2026 Interactive Map
      </h1>
      <div className={styles.callToAction}>
        <p
          id="messageButtonHelperText"
          className={styles.instruction + " " + styles.disabled}
        >
          Share your appreciation
        </p>
        {/*
          Submissions are closed, so this is a disabled button rather than a
          link to FORM_URL: it stays in place as the campaign's CTA but can't
          be followed, and assistive tech announces it as unavailable. Restore
          the <a href={FORM_URL}> when the form reopens.
        */}
        <button
          type="button"
          className={styles.button + " " + styles.disabled}
          aria-describedby="messageButtonHelperText"
          disabled
        >
          Message
        </button>
        {counterText && (
          <p className={styles.counter}>
            <strong>{animatedValue}</strong> {counterText}
          </p>
        )}
      </div>
      <img
        className={styles.motif}
        id={styles.twibbonMotif}
        src={MEDIA_URL + "/img/WTD2026-Twibbon.svg"}
        alt="icons and hashtag #WTD2026"
      />
    </div>
  );
};
export default Header;
